/* eslint-disable default-case */
import {
  PackageItem,
} from '@/features/shipment/types';
import store from '@/features/common/store';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { shipmentAdvisorNotificationStatus } from '@/features/shipment/services/advisor.services';
import { NotificationData } from '@/features/settings/types';
import { FreightLimit } from '@/features/common/raw';
import { isEnabledPref } from '.';

export const getTotalInsurance = (packageList: Array<PackageItem>): number => {
  return packageList.reduce((a: number, b: PackageItem): number => {
    return a + (+b.insurance);
  }, 0);
};

export const getIsFreight = (packageList: Array<PackageItem>): boolean => {
  for (const packageItem of packageList) {
    const { weight, weightUnits, packageType } = packageItem;

    if (packageType === 'Pallet') return true;

    switch (weightUnits) {
      case 'Lbs':
        if (weight > FreightLimit.Lbs) return true;
        break;
      case 'Kgs':
        if (weight > FreightLimit.Kgs) return true;
        break;
    }
  }

  return false;
};

const findPackage = (packageList: Array<PackageItem>, toFind: string): boolean => {
  let output = false;

  packageList.forEach((el: PackageItem) => {
    if (el.packageType === toFind) {
      output = true;
    }
  });

  return output;
};

const getIsLargePackage = (packageList: Array<PackageItem>): boolean => {
  for (const item of packageList) {
    const {
      length, width, height, dimUnits,
    } = item;
    const girth1 = length + width * 2 + height * 2;
    const girth2 = length * 2 + width * 2 + height;
    const girth3 = length * 2 + width + height * 2;
    const minGirth = Math.min(girth1, girth2, girth3);
    const oversizeCm = length >= 200 || width >= 200 || height >= 200;
    const oversizeInches = length >= 80 || width >= 80 || height >= 80;

    switch (dimUnits) {
      case 'CM':
        if (minGirth >= 290 || oversizeCm) return true;
        break;
      case 'Inches':
        if (minGirth >= 115 || oversizeInches) return true;
        break;
    }
  }

  return false;
};

const getIsOverLimit = (packageList: Array<PackageItem>): boolean => {
  for (const item of packageList) {
    const {
      weight, width, length, height, dimUnits, weightUnits,
    } = item;
    const girth1 = length + width * 2 + height * 2;
    const girth2 = length * 2 + width * 2 + height;
    const girth3 = length * 2 + width + height * 2;
    const minGirth = Math.min(girth1, girth2, girth3);
    const overweightLbs = weight >= 130;
    const overweightKgs = weight >= 50;
    const oversizeCm = length >= 245 || width >= 245 || height >= 245;
    const oversizeInches = length >= 96 || width >= 96 || height >= 96;

    switch (weightUnits) {
      case 'Lbs':
        if (overweightLbs) return true;
        break;
      case 'Kgs':
        if (overweightKgs) return true;
        break;
    }

    switch (dimUnits) {
      case 'CM':
        if (minGirth >= 380 || oversizeCm) return true;
        break;
      case 'Inches':
        if (minGirth >= 145 || oversizeInches) return true;
        break;
    }
  }

  return false;
};

const getIsNoDimensions = (packageList: PackageItem[]): boolean => {
  if (!packageList || !packageList.length) return false;

  const noDimensionPackage = packageList.find(
    (packageItem) => packageItem.packageType === 'MyPackage' && (!packageItem.length || !packageItem.width || !packageItem.height),
  );
  return !!noDimensionPackage;
};

export const getCreateShipmentNotifications = async (
  packageList: Array<PackageItem>,
  onSubmit: () => Promise<void>,
): Promise<void> => {
  const advisorNotifications = await shipmentAdvisorNotificationStatus();
  const notificationSettings = store.getters.getNotificationPrefs;

  const notificationData: Array<NotificationData> = [];
  const totalInsurance = getTotalInsurance(packageList);
  const isMyPackage = findPackage(packageList, 'My Package');
  const isEnvelopePackage = findPackage(packageList, 'Envelope');
  const isPakPackage = findPackage(packageList, 'Pak');
  const isLargePackage = !getIsFreight(packageList) && getIsLargePackage(packageList);
  const isOverLimit = !getIsFreight(packageList) && getIsOverLimit(packageList);
  const isNoDimensions = getIsNoDimensions(packageList);

  if (totalInsurance > 100 && isEnabledPref(notificationSettings, 'InsuranceLimitations')) {
    notificationData.push({
      name: 'InsuranceLimitations',
    });
  }

  if (isNoDimensions && isEnabledPref(notificationSettings, 'DimensionsMissing')) {
    notificationData.push({ name: 'DimensionsMissing' });
  }

  if (advisorNotifications?.isPoBox && isEnabledPref(notificationSettings, 'POBox')) {
    notificationData.push({ name: 'POBox' });
  }

  if (advisorNotifications?.isFragile && isEnabledPref(notificationSettings, 'IsFragile')) {
    notificationData.push({ name: 'IsFragile' });
  }

  if (isMyPackage && isEnabledPref(notificationSettings, 'PickupNotMonitored')) {
    notificationData.push({ name: 'PickupNotMonitored' });
  }

  if (isLargePackage && isEnabledPref(notificationSettings, 'RiskOfLargePackage')) {
    notificationData.push({ name: 'RiskOfLargePackage' });
  }

  if (isOverLimit && isEnabledPref(notificationSettings, 'RiskOfOverLimit')) {
    notificationData.push({ name: 'RiskOfOverLimit' });
  }

  if (isEnvelopePackage && isEnabledPref(notificationSettings, 'CarrierEnv')) {
    notificationData.push({ name: 'CarrierEnv' });
  }

  if (isPakPackage && isEnabledPref(notificationSettings, 'CarrierPak')) {
    notificationData.push({ name: 'CarrierPak' });
  }

  if (
    advisorNotifications?.isSignatureRequested
    && isEnabledPref(notificationSettings, 'IsSignatureRequested')
  ) {
    notificationData.push({ name: 'IsSignatureRequested' });
  }

  if (
    advisorNotifications?.isPickupComments
    && isEnabledPref(notificationSettings, 'IsPickupComments')
  ) {
    notificationData.push({ name: 'IsPickupComments' });
  }

  if (
    advisorNotifications?.isCallRequested
    && isEnabledPref(notificationSettings, 'IsCallRequested')
  ) {
    notificationData.push({ name: 'IsCallRequested' });
  }

  if (notificationData.length) {
    store.dispatch('showNotification', notificationData);
    eventBus.$once(eventNames.NOTIFICATION_SUBMIT, onSubmit);
  } else {
    onSubmit();
  }
};
